import { FC, memo, Suspense } from "react";
import { Helmet } from "react-helmet";
import { useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { Fiat, i18n, IconDenom } from "rujira.ui";

import { useNavigate } from "react-router-dom";
import { Subscription } from "../services/useNodeSubscription";
import {
  IndexesQuery,
  IndexesQuery$data,
} from "./__generated__/IndexesQuery.graphql";
import { DailyChange } from "./components/Common";
import { Balances } from "./components/IndexBalances";

const Meta = () => {
  return (
    <Helmet>
      <title>Rujira Index | Native On-Chain Crypto Indexes</title>
      <meta
        name="description"
        content="Products for investors, traders and institutions looking for diversified market access without the complexity."
      />
      <meta
        name="keywords"
        content="Rujira, Crypto Index, Decentralized Finance, DeFi, Asset Allocation, Portfolio, Blockchain, Investment, Crypto Portfolio, Index Fund"
      />
      <meta
        property="og:title"
        content="Rujira Index | Native On-Chain Crypto Indexes"
      />
      <meta
        property="og:description"
        content="Products for investors, traders and institutions looking for diversified market access without the complexity."
      />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="/index/index-graph.png" />
      <link rel="canonical" href="https://rujira.app/index" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Rujira Index | Native On-Chain Crypto Indexes"
      />
      <meta
        name="twitter:description"
        content="Products for investors, traders and institutions looking for diversified market access without the complexity."
      />
      <meta name="twitter:image" content="/index/index-graph.png" />
      <meta name="twitter:site" content="@RujiraNetwork" />
    </Helmet>
  );
};

const query = graphql`
  query IndexesQuery {
    index {
      id
      status {
        nav
        navPerShareChange
      }
      shareAsset {
        metadata {
          decimals
          symbol
          description
          name
        }
      }
    }
  }
`;

export const Indexes: FC = () => {
  return (
    <>
      <Meta />
      <div className="rujira__main rujira__main--gradient index">
        <div className="rujira__inner--pad">
          <div className="rujira__inner">
            <div className="index__container">
              <div className="index__main">
                <i18n.h1 className="h1">Index</i18n.h1>
                <i18n.h2 className="fs-24 lh-32 fw-400 color-white index__main__subheading">
                  Products for investors, traders and institutions looking for
                  diversified market access without the complexity.
                </i18n.h2>
                <IndexesGrid />
              </div>
              <Balances />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const IndexesGrid: FC = () => {
  const data = useLazyLoadQuery<IndexesQuery>(query, {});
  const items = data?.index ?? [];
  return (
    <Suspense fallback={<Fallback />}>
      <div className="index__grid mt-3">
        {items.map((x) =>
          x ? <MemoizedIndexCard key={x.id} data={x} /> : null
        )}
      </div>
    </Suspense>
  );
};

const indexesSubscription = graphql`
  subscription IndexesSubscription($id: ID!) {
    node(id: $id) {
      ... on IndexVault {
        status {
          nav
          navPerShareChange
        }
      }
    }
  }
`;

const IndexCard: FC<{ data: IndexesQuery$data["index"][number] }> = ({
  data,
}) => {
  const navigate = useNavigate();
  if (!data) return null;
  const url = `/index/${data.shareAsset.metadata.symbol}`;

  return (
    <>
      <Subscription id={data.id} subscription={indexesSubscription} />
      <div
        className="card p-3 card--shadow"
        style={{ cursor: "pointer" }}
        onClick={() => navigate(url)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") navigate(url);
        }}
        role="button"
        aria-label={`View index ${data.shareAsset.metadata.symbol}`}>
        <div className="row ai-c ji-c mb-2">
          <IconDenom
            denom={data.shareAsset.metadata.symbol}
            className="index__denomIcon"
          />
          <i18n.h4 className="ml-2">
            {data.shareAsset.metadata.symbol} - {data.shareAsset.metadata.name}
          </i18n.h4>
        </div>
        <div className="flex dir-c index__card-text">
          <i18n.p>{data.shareAsset.metadata.description}</i18n.p>
          <div className="row ai-s ji-c pt-2">
            <div className="col-12 flex dir-c">
              <i18n.label className="h4 fs-16 lh-19 color-grey mb-1 fw-400">
                Market Cap
              </i18n.label>
              <Fiat
                amount={BigInt(data.status.nav || "0")}
                symbol="$"
                decimals={data.shareAsset.metadata.decimals}
                className="index__decimal"
              />
            </div>
            <div className="col-12 flex dir-c">
              <i18n.label className="h4 fs-16 lh-19 color-grey mb-1 fw-400">
                24h Price Change
              </i18n.label>
              <DailyChange
                className="index__decimal"
                percentageChange={BigInt(data.status.navPerShareChange || "0")}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const MemoizedIndexCard = memo(IndexCard);

const Fallback: FC = () => (
  <div
    className="card p-3 card--shadow"
    style={{
      minHeight: 120,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}>
    <div className="row ai-c ji-c mb-2">
      <div
        className="icon-denom skeleton br-4"
        style={{ width: 36, height: 36 }}
      />
      <div className="skeleton br-2 ml-2" style={{ width: 120, height: 20 }} />
    </div>
    <div className="flex dir-c index__card-text">
      <div
        className="skeleton br-2 mb-2"
        style={{ width: "80%", height: 16 }}
      />
      <div className="row ai-s ji-c pt-2">
        <div className="col-12 flex dir-c mr-4">
          <div
            className="skeleton br-2 mb-1"
            style={{ width: 60, height: 14 }}
          />
          <div className="skeleton br-2" style={{ width: 80, height: 18 }} />
        </div>
        <div className="col-12 flex dir-c">
          <div
            className="skeleton br-2 mb-1"
            style={{ width: 90, height: 14 }}
          />
          <div className="skeleton br-2" style={{ width: 60, height: 18 }} />
        </div>
      </div>
    </div>
  </div>
);
